import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchTrainingCategories, fetchTrainingStaff, saveTrainingRecord } from '../../../features/staffTraining/staffTrainingActions';
import { selectCanEditTraining } from '../../../features/staffTraining/staffTrainingSelectors';
import { getLocalTodayString } from '../../../components/CreationDate';
import Toast from '../../../components/Toast';
import useTrainingRecord from '../hooks/useTrainingRecord';
const Context = createContext(null);
const initial = () => ({ staffId: '', courseLevel: '', trainedOn: getLocalTodayString(), categories: [] });
function FormProvider({ id, recordState, children }) {
  const dispatch = useDispatch(), navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => ({ ...initial(), staffId: !id && /^\d+$/.test(params.get('staffId') || '') ? params.get('staffId') : '' })), [error, setError] = useState(null), [ready, setReady] = useState(!id);
  const initialized = useRef(null), inFlight = useRef(false);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const categoryState = useSelector(state => state.staffTraining.categories);
  const staffState = useSelector(state => state.staffTraining.staff);
  const saving = useSelector(state => state.staffTraining.saving);
  const canEdit = useSelector(selectCanEditTraining);
  const record = recordState?.data;
  useEffect(() => {
    const categoriesRequest = dispatch(fetchTrainingCategories());
    const usersRequest = dispatch(fetchTrainingStaff());
    return () => { categoriesRequest.abort(); usersRequest.abort(); };
  }, [dispatch]);
  useEffect(() => {
    if (id && record && !recordState.loading && initialized.current !== id) {
      setForm({ staffId: String(record.staffId), courseLevel: record.courseLevel, trainedOn: record.trainedOn.slice(0, 10), version: record.version,
        categories: record.categories.map(row => ({ categoryId: row.categoryId, expiresOn: row.expiresOn?.slice(0, 10) || '' })) });
      initialized.current = id; setReady(true);
    }
  }, [id, record, recordState?.loading]);
  const categories = [...(categoryState.data || [])];
  record?.categories.forEach(row => { if (!categories.some(item => item.id === row.categoryId)) categories.push(row.category); });
  const staff = (staffState.data || []).filter(person => person.status === 'ACTIVE' && (!person.trainingRecordId || String(person.trainingRecordId) === String(id)));
  if (record?.staff && !staff.some(person => person.id === record.staffId)) staff.push(record.staff);
  const occupiedStaff = !id && (staffState.data || []).find(person => String(person.id) === form.staffId && person.trainingRecordId);
  const update = (field, value) => { setForm(previous => ({ ...previous, [field]: value })); setError(null); };
  const toggleCategory = categoryId => setForm(previous => ({ ...previous, categories: previous.categories.some(row => row.categoryId === categoryId)
    ? previous.categories.filter(row => row.categoryId !== categoryId) : [...previous.categories, { categoryId, expiresOn: '' }] }));
  const save = async event => {
    event.preventDefault();
    if (saving || inFlight.current || !canEdit) return;
    if (occupiedStaff) { setError('This staff member already has a training record. Edit the existing record instead.'); return; }
    if (!form.staffId || !form.courseLevel || !form.trainedOn || !form.categories.length) { setError('Select a staff member, course level, training date and at least one category.'); return; }
    if (form.trainedOn > getLocalTodayString()) { setError('Training date cannot be in the future.'); return; }
    if (form.categories.some(row => row.expiresOn && row.expiresOn < form.trainedOn)) { setError('Expiry dates cannot be before the training date.'); return; }
    inFlight.current = true; setError(null);
    try {
      const result = await dispatch(saveTrainingRecord({ id, data: { ...form, staffId: Number(form.staffId) } })).unwrap();
      if (!active.current) return;
      Toast('success', id ? 'Training record updated' : 'Training record added');
      navigate('/staff-training/records/' + result.id, { replace: true });
    } catch (message) { if (active.current) setError(String(message)); }
    finally { inFlight.current = false; }
  };
  return <Context.Provider value={{ form, update, toggleCategory, categories, staff, saving, save, canEdit,
    loading: !ready || categoryState.loading || staffState.loading || Boolean(id && recordState?.loading),
    error: error || recordState?.error || categoryState.error || staffState.error,
    loadError: Boolean(recordState?.error || categoryState.error || staffState.error), occupiedRecordId: occupiedStaff?.trainingRecordId, id }}>
    {children}
  </Context.Provider>;
}
function EditProvider({ id, children }) { const recordState = useTrainingRecord(id); return <FormProvider key={id} id={id} recordState={recordState}>{children}</FormProvider>; }
export function TrainingRecordFormProvider({ id, children }) { return id ? <EditProvider id={id}>{children}</EditProvider> : <FormProvider>{children}</FormProvider>; }
export const useTrainingRecordForm = () => useContext(Context);
